'use client';

import { c as _c } from "react/compiler-runtime";
import { ChartNoDataEmptyState } from '@/comps/charts/analytics-chart/ChartNoDataEmptyState';
import { AIFI_CHART_WITHOUT_ANIMATION } from '@/domains/pages/ai-proficiency/constants';
import { buildTaskModelTooltipHtml } from '@/domains/pages/ai-spend/components/spend-chart-tooltip';
import { SPEND_BY_MODEL_CHART_MIN_HEIGHT_CLASS, SPEND_BY_MODEL_TASK_COLUMN_MIN_WIDTH_PX } from '@/domains/pages/ai-spend/constants';
import { computeTotalSpendAxisBounds } from '@/domains/pages/ai-spend/utils/ai-spend-over-time.utils';
import { computeSpendByModelChartWidthPx } from '@/domains/pages/ai-spend/utils/spend-by-model-chart-width';
import { applyVerticalBarGradientFills, verticalBarGradientPlugin } from '@/domains/pages/ai-spend/utils/bar-fill';
import { useGetElementSize } from '@/hooks/useGetElementSize';
import type { TaskModelSeries } from '@/domains/pages/ai-spend/utils/map-ai-spend-task-model';
import { positionChartExternalTooltip } from '@/domains/pages/shared/chart-external-tooltip';
import { themeColors } from '@/theme/palette';
import { jetbrainsMonoChartFontFamily } from '@/theme/utils/font.utils';
import { formatChartCurrencyMetricValue } from '@/utils/format.util';
import { cn } from '@/utils/shad';
import { BarController, BarElement, CategoryScale, Chart as ChartJS, ChartData, ChartOptions, LinearScale } from 'chart.js';
import { useEffect, useMemo, useRef } from 'react';
import { Bar } from 'react-chartjs-2';
ChartJS.register(BarController, BarElement, LinearScale, CategoryScale, verticalBarGradientPlugin);
const TASK_MODEL_TOOLTIP_ID = 'ai-spend-task-model-tooltip';
const TASK_LABEL_MAX_LENGTH = 25;

/** Whole task column is hoverable; tooltip resolves all models via dataIndex. */
const BAR_INTERACTION = {
  mode: 'index' as const,
  axis: 'x' as const,
  intersect: false
};
function hasBarValue(value: unknown): boolean {
  return typeof value === 'number' && Number.isFinite(value);
}
const HIDDEN_DATALABELS = {
  datalabels: {
    display: false
  }
};
function truncateTaskLabel(label: string): string {
  if (label.length <= TASK_LABEL_MAX_LENGTH) {
    return label;
  }
  return `${label.slice(0, TASK_LABEL_MAX_LENGTH - 3)}...`;
}
function collectAxisValues(series: TaskModelSeries[]): number[] {
  return series.flatMap(item => item.data.filter((value): value is number => value != null));
}
function computeSpendPerTask(cost: number | null, tasks: number | null): number | null {
  if (cost == null || tasks == null || tasks <= 0) {
    return null;
  }
  return cost / tasks;
}
type SpendByModelChartProps = {
  taskLabels: string[];
  series: TaskModelSeries[];
  className?: string;
};
export function SpendByModelChart(t0) {
  const $ = _c(69);
  const {
    taskLabels,
    series,
    className
  } = t0;
  const chartRef = useRef(null);
  const {
    ref: scrollContainerRef,
    size: scrollContainerSize
  } = useGetElementSize();
  let t1;
  if ($[0] !== series) {
    t1 = series.map(_temp);
    $[0] = series;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const seriesColors = t1;
  const hasData = taskLabels.length > 0 && series.some(_temp3);
  let t2;
  if ($[2] !== scrollContainerSize.width || $[3] !== taskLabels.length) {
    t2 = computeSpendByModelChartWidthPx(scrollContainerSize.width, taskLabels.length, SPEND_BY_MODEL_TASK_COLUMN_MIN_WIDTH_PX);
    $[2] = scrollContainerSize.width;
    $[3] = taskLabels.length;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const chartWidthPx = t2;
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  useEffect(_temp5, t3);
  let t4;
  if ($[6] !== seriesColors) {
    t4 = () => {
      const frame = requestAnimationFrame(() => {
        const chart = chartRef.current;
        if (!chart) {
          return;
        }
        applyVerticalBarGradientFills(chart, seriesColors);
        chart.draw();
      });
      return () => cancelAnimationFrame(frame);
    };
    $[6] = seriesColors;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== chartWidthPx || $[9] !== series || $[10] !== seriesColors || $[11] !== taskLabels) {
    t5 = [series, seriesColors, taskLabels, chartWidthPx];
    $[8] = chartWidthPx;
    $[9] = series;
    $[10] = seriesColors;
    $[11] = taskLabels;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  useEffect(t4, t5);
  let t6;
  if ($[13] !== series) {
    t6 = computeTotalSpendAxisBounds(collectAxisValues(series));
    $[13] = series;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const yAxisBounds = t6;
  let t7;
  if ($[15] !== series) {
    t7 = series.map(_temp6);
    $[15] = series;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t7 || $[18] !== taskLabels) {
    t8 = {
      labels: taskLabels,
      datasets: t7
    };
    $[17] = t7;
    $[18] = taskLabels;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const chartData = t8;
  let t10;
  let t11;
  let t9;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      padding: {
        top: 0,
        right: 8,
        left: 0,
        bottom: 0
      }
    };
    t10 = {
      display: false
    };
    t11 = {
      display: false
    };
    $[20] = t10;
    $[21] = t11;
    $[22] = t9;
  } else {
    t10 = $[20];
    t11 = $[21];
    t9 = $[22];
  }
  let t12;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      size: 11
    };
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== taskLabels) {
    t13 = {
      grid: t10,
      border: t11,
      ticks: {
        color: themeColors.text.secondary,
        font: t12,
        maxRotation: 45,
        minRotation: 45,
        callback: (_, index) => truncateTaskLabel(taskLabels[index] ?? "")
      }
    };
    $[24] = taskLabels;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  let t15;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      display: true,
      color: "rgba(255, 255, 255, 0.12)"
    };
    t15 = {
      color: "rgba(255, 255, 255, 0.08)",
      tickLength: 4
    };
    $[26] = t14;
    $[27] = t15;
  } else {
    t14 = $[26];
    t15 = $[27];
  }
  let t16;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      size: 11,
      family: jetbrainsMonoChartFontFamily
    };
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== yAxisBounds.stepSize) {
    t17 = {
      display: true,
      color: themeColors.text.secondary,
      font: t16,
      padding: 6,
      stepSize: yAxisBounds.stepSize,
      callback: _temp7
    };
    $[29] = yAxisBounds.stepSize;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== t17 || $[32] !== yAxisBounds.max || $[33] !== yAxisBounds.min) {
    t18 = {
      position: "left",
      min: yAxisBounds.min,
      max: yAxisBounds.max,
      border: t14,
      grid: t15,
      ticks: t17
    };
    $[31] = t17;
    $[32] = yAxisBounds.max;
    $[33] = yAxisBounds.min;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== t13 || $[36] !== t18) {
    t19 = {
      x: t13,
      y: t18
    };
    $[35] = t13;
    $[36] = t18;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = {
      display: false
    };
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== seriesColors) {
    t21 = {
      color: seriesColors
    };
    $[39] = seriesColors;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== series || $[42] !== taskLabels) {
    t22 = {
      enabled: false,
      filter: _temp8,
      external(context) {
        let tooltipEl = document.getElementById(TASK_MODEL_TOOLTIP_ID);
        if (!tooltipEl) {
          tooltipEl = document.createElement("div");
          tooltipEl.id = TASK_MODEL_TOOLTIP_ID;
        }
        const tooltipModel = context.tooltip;
        if (tooltipModel.opacity === 0) {
          tooltipEl.style.opacity = "0";
          return;
        }
        const index_0 = tooltipModel.dataPoints?.[0]?.dataIndex ?? 0;
        const rows = series.map(item_3 => {
          const spend = item_3.data[index_0] ?? null;
          const tasks = item_3.tasksPerPoint[index_0] ?? null;
          return {
            model: item_3.label,
            spend,
            spendPerTask: computeSpendPerTask(spend, tasks),
            color: item_3.color
          };
        }).filter(_temp9);
        tooltipEl.innerHTML = buildTaskModelTooltipHtml({
          taskName: taskLabels[index_0] ?? "",
          rows
        });
        positionChartExternalTooltip(context.chart, tooltipEl, tooltipModel);
      }
    };
    $[41] = series;
    $[42] = taskLabels;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  let t23;
  if ($[44] !== t21 || $[45] !== t22) {
    t23 = {
      ...HIDDEN_DATALABELS,
      legend: t20,
      verticalBarGradient: t21,
      tooltip: t22
    };
    $[44] = t21;
    $[45] = t22;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] !== t19 || $[48] !== t23) {
    t24 = {
      ...AIFI_CHART_WITHOUT_ANIMATION,
      responsive: true,
      maintainAspectRatio: false,
      interaction: BAR_INTERACTION,
      hover: BAR_INTERACTION,
      layout: t9,
      scales: t19,
      plugins: t23
    };
    $[47] = t19;
    $[48] = t23;
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  const options = t24;
  if (!hasData) {
    let t25;
    if ($[50] !== className) {
      t25 = cn("relative h-full w-full overflow-hidden", SPEND_BY_MODEL_CHART_MIN_HEIGHT_CLASS, className);
      $[50] = className;
      $[51] = t25;
    } else {
      t25 = $[51];
    }
    let t26;
    if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
      t26 = <ChartNoDataEmptyState className="h-full" />;
      $[52] = t26;
    } else {
      t26 = $[52];
    }
    let t27;
    if ($[53] !== t25) {
      t27 = <div className={t25} data-testid="spend-by-model-chart">{t26}</div>;
      $[53] = t25;
      $[54] = t27;
    } else {
      t27 = $[54];
    }
    return t27;
  }
  let t25;
  if ($[55] !== className) {
    t25 = cn("relative flex w-full overflow-x-auto overflow-y-hidden", SPEND_BY_MODEL_CHART_MIN_HEIGHT_CLASS, className);
    $[55] = className;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  let t26;
  if ($[57] !== chartWidthPx) {
    t26 = {
      width: chartWidthPx
    };
    $[57] = chartWidthPx;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  let t27;
  if ($[59] !== chartData || $[60] !== options) {
    t27 = <Bar ref={chartRef} data={chartData} options={options} />;
    $[59] = chartData;
    $[60] = options;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  let t28;
  if ($[62] !== t26 || $[63] !== t27) {
    t28 = <div className="min-w-full shrink-0" style={t26}>{t27}</div>;
    $[62] = t26;
    $[63] = t27;
    $[64] = t28;
  } else {
    t28 = $[64];
  }
  let t29;
  if ($[65] !== scrollContainerRef || $[66] !== t25 || $[67] !== t28) {
    t29 = <div ref={scrollContainerRef} className={t25} data-testid="spend-by-model-chart">{t28}</div>;
    $[65] = scrollContainerRef;
    $[66] = t25;
    $[67] = t28;
    $[68] = t29;
  } else {
    t29 = $[68];
  }
  return t29;
}
function _temp9(row) {
  return row.spend != null;
}
function _temp8(item_2) {
  return hasBarValue(item_2.parsed.y);
}
function _temp7(value_0) {
  return formatChartCurrencyMetricValue(Number(value_0));
}
function _temp6(item_1) {
  return {
    label: item_1.label,
    data: item_1.data,
    backgroundColor: item_1.color,
    borderColor: item_1.color,
    borderWidth: 1,
    borderRadius: 3,
    borderSkipped: false,
    minBarLength: 1,
    skipNull: true,
    categoryPercentage: 0.8,
    barPercentage: 0.9,
    maxBarThickness: 60
  };
}
function _temp5() {
  return _temp4;
}
function _temp4() {
  document.getElementById(TASK_MODEL_TOOLTIP_ID)?.remove();
}
function _temp3(item_0) {
  return item_0.data.some(_temp2);
}
function _temp2(value) {
  return value != null;
}
function _temp(item) {
  return item.color;
}